import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, User2, Building2, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLES = [
  "Do I know anyone who could help with film distribution?",
  "Who do we know through Alias Distribution?",
  "What organizations do we know in Ethiopia?",
  "Who could help produce a podcast?",
];

export default function AskHQPage() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  const ask = async (q) => {
    const query = (q || question).trim();
    if (!query) return toast.error("Type a question first");
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(`${API}/ask`, { question: query },
        { headers: { Authorization: `Bearer ${token}` } });
      setAnswer(r.data);
      if (q) setQuestion(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ask HQ failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden" data-testid="ask-hq-page">
      <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-white/70"/>
            <h1 className="text-3xl font-display font-semibold tracking-tight">Ask HQ</h1>
          </div>
          <p className="text-sm text-white/50 mt-1 max-w-2xl">
            Query the relationship graph — people, organizations, and connections HQ already knows about.
            Ask HQ never invents facts and never takes action; it only answers.
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
          <Textarea value={question} onChange={e => setQuestion(e.target.value)}
            rows={2} placeholder="Do I know anyone who could distribute this film?"
            className="bg-white/5 border-white/10 text-sm"
            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }}
            data-testid="ask-hq-input"/>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex flex-wrap gap-1.5">
              {SAMPLES.map(s => (
                <button key={s} onClick={() => ask(s)}
                  className="text-[11px] px-2 py-1 rounded-full border border-white/10 bg-white/[0.03] text-white/60 hover:text-white hover:border-white/25"
                  data-testid={`ask-hq-sample-${s.slice(0, 20).replace(/\W+/g, "-")}`}>
                  {s}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={() => ask()} disabled={loading || !question.trim()}
              className="rounded-full bg-white text-black hover:bg-white/90"
              data-testid="ask-hq-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Sparkles className="w-4 h-4 mr-1"/>}
              {loading ? "Asking…" : "Ask"}
            </Button>
          </div>
        </div>

        {answer && (
          <div className="space-y-4">
            <div className="text-sm text-white/80" data-testid="ask-hq-summary">{answer.answer_summary}</div>
            {answer.empty ? (
              <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] p-8 text-center text-white/50 text-sm"
                   data-testid="ask-hq-empty">
                No strong known relationship yet. Try a different phrasing, or capture more context via Intake.
              </div>
            ) : (
              <div className="space-y-3">
                {answer.results.map(r => (
                  <ResultCard key={`${r.kind}-${r.id}`} r={r}/>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCard({ r }) {
  const Icon = r.kind === "organization" ? Building2 : User2;
  const link = r.kind === "organization"
    ? `/organizations/${r.id}` : `/contacts/${r.id}`;
  return (
    <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]"
         data-testid={`ask-hq-result-${r.kind}-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-white/70"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Link to={link} className="text-white font-medium hover:underline">
              {r.full_name || r.name}
            </Link>
            <div className="flex items-center gap-2 text-[11px] text-white/50">
              {r.best_status && <Badge variant="outline" className="border-white/15 bg-white/5 text-white/70">{r.best_status}</Badge>}
              <span title={`Source ${Math.round(r.source_score * 100)}% + Context +${Math.round(r.context_boost * 100)}`}>
                {Math.round(r.final_score * 100)}%
              </span>
            </div>
          </div>
          {(r.facts || []).length > 0 && (
            <div className="text-xs text-white/60 mt-1">{r.facts.join(" · ")}</div>
          )}
          {(r.connections || []).length > 0 && (
            <div className="mt-2 space-y-0.5" data-testid={`ask-hq-connections-${r.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-white/40">Connections</div>
              {r.connections.map((c, i) => (
                <div key={i} className="text-xs text-white/75 inline-flex items-center gap-1">
                  <ArrowRight className="w-3 h-3 text-white/30"/> {c}
                </div>
              ))}
            </div>
          )}
          {(r.possible_fits || []).length > 0 && (
            <div className="mt-2" data-testid={`ask-hq-possible-fits-${r.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Possible fit</div>
              {r.possible_fits.map((p, i) => (
                <div key={i} className="text-xs text-white/70 italic">{p}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
